import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  TextInput,
  Alert,
  Image,
  Dimensions,
  ScrollView,
  ActivityIndicator,
  Platform,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';
import { VehicleDamage } from '../../types';
import { uploadDamagePhoto, deleteDamagePhoto } from '../../api/storage';
import theme from '../../theme';

const VEHICLE_DIAGRAM_URL = 'https://ik.imagekit.io/to2pwwjig/Tesla%20Model%20S%20-%20Free%20CAD%20Drawings.jpeg?updatedAt=1750739302834';

interface VehicleDamagePickerProps {
  damages: VehicleDamage[];
  onDamagesChange: (damages: VehicleDamage[]) => void;
  editable?: boolean;
  containerWidth?: number;
  containerHeight?: number;
}

interface DamageMarker {
  id: string;
  x: number;
  y: number;
  type: 'scratch' | 'dent' | 'missing' | 'broken' | 'crack' | 'other';
  severity: 'minor' | 'moderate' | 'major';
  description?: string;
  photoUrl?: string; // Added photo support
}

const DAMAGE_TYPES = [
  { value: 'scratch', label: 'Scratch / خدش' },
  { value: 'dent', label: 'Dent / انبعاج' },
  { value: 'crack', label: 'Crack / شرخ' },
  { value: 'broken', label: 'Broken / مكسور' },
  { value: 'missing', label: 'Missing / مفقود' },
  { value: 'other', label: 'Other / أخرى' },
];

const DAMAGE_SEVERITY = [
  { value: 'minor', label: 'Minor / طفيف', color: '#44aa44' },
  { value: 'moderate', label: 'Moderate / متوسط', color: '#ffaa00' },
  { value: 'major', label: 'Major / رئيسي', color: '#ff4444' },
];

export const VehicleDamagePicker: React.FC<VehicleDamagePickerProps> = ({
  damages = [],
  onDamagesChange,
  editable = true,
  containerWidth = 220,
  containerHeight = 180,
}) => {
  const [showDamageModal, setShowDamageModal] = useState(false);
  const [pendingDamage, setPendingDamage] = useState<Partial<DamageMarker> | null>(null);
  const [damageType, setDamageType] = useState<string>('scratch');
  const [damageSeverity, setDamageSeverity] = useState<string>('minor');
  const [damageDescription, setDamageDescription] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string>('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const diagramRef = useRef<View>(null);

  // Convert VehicleDamage[] to DamageMarker[] for rendering
  const markers: DamageMarker[] = damages.map((d) => ({
    id: d.id || `damage-${Date.now()}-${Math.random()}`,
    x: d.x_coordinate || 0,
    y: d.y_coordinate || 0,
    type: (d.damageType || 'other') as DamageMarker['type'],
    severity: (d.damageSeverity || 'minor') as DamageMarker['severity'],
    description: d.damageDescription,
    photoUrl: d.photoUrl,
  }));

  const handleDiagramPress = (event: any) => {
    console.log('👆 Diagram pressed');
    
    if (!editable) {
      console.log('⚠️ Not editable, ignoring press');
      return;
    }

    // For web and native, we need to get coordinates differently
    let locationX: number | undefined;
    let locationY: number | undefined;

    if (Platform.OS === 'web') {
      // On web, use getBoundingClientRect for accurate positioning
      const nativeEvent = event.nativeEvent;
      
      if (diagramRef.current) {
        // Use the native DOM element
        const element = (diagramRef.current as any)._touchableNode || diagramRef.current;
        
        if (element && element.getBoundingClientRect) {
          const rect = element.getBoundingClientRect();
          
          console.log('📏 Diagram rect:', { 
            top: rect.top, 
            left: rect.left, 
            width: rect.width, 
            height: rect.height 
          });
          
          // Calculate relative coordinates using client position
          const relativeX = nativeEvent.clientX - rect.left;
          const relativeY = nativeEvent.clientY - rect.top;
          
          console.log('📍 Web press location:', { 
            clientX: nativeEvent.clientX, 
            clientY: nativeEvent.clientY,
            relativeX, 
            relativeY 
          });
          
          // Ensure coordinates are within bounds
          if (relativeX < 0 || relativeY < 0 || relativeX > containerWidth || relativeY > containerHeight) {
            console.log('⚠️ Press outside bounds:', { relativeX, relativeY, containerWidth, containerHeight });
            return;
          }

          const newPendingDamage = {
            id: `damage-${Date.now()}`,
            x: relativeX,
            y: relativeY,
          };
          
          console.log('✅ Setting pending damage:', newPendingDamage);
          setPendingDamage(newPendingDamage);
          setDamageType('scratch');
          setDamageSeverity('minor');
          setDamageDescription('');
          setPhotoUrl('');
          setShowDamageModal(true);
          return;
        }
      }
      
      // Fallback: use offsetX/offsetY if available
      locationX = nativeEvent.offsetX;
      locationY = nativeEvent.offsetY;
      
      console.log('📍 Web fallback location:', { locationX, locationY });
      
      if (locationX === undefined || locationY === undefined) {
        console.error('❌ Could not determine click coordinates on web');
        Alert.alert('Error', 'Could not determine click location. Please try again.');
        return;
      }

      // Continue with the fallback coordinates
      if (locationX < 0 || locationY < 0 || locationX > containerWidth || locationY > containerHeight) {
        console.log('⚠️ Press outside bounds:', { locationX, locationY, containerWidth, containerHeight });
        return;
      }

      const newPendingDamage = {
        id: `damage-${Date.now()}`,
        x: locationX,
        y: locationY,
      };
      
      console.log('✅ Setting pending damage:', newPendingDamage);
      setPendingDamage(newPendingDamage);
      setDamageType('scratch');
      setDamageSeverity('minor');
      setDamageDescription('');
      setPhotoUrl('');
      setShowDamageModal(true);
    } else {
      // Native: use locationX/locationY from nativeEvent
      locationX = event.nativeEvent.locationX;
      locationY = event.nativeEvent.locationY;
      
      console.log('📍 Native press location:', { locationX, locationY });
      
      // Ensure coordinates are within bounds
      if (locationX === undefined || locationY === undefined || 
          locationX < 0 || locationY < 0 || 
          locationX > containerWidth || locationY > containerHeight) {
        console.log('⚠️ Press outside bounds or undefined:', { locationX, locationY, containerWidth, containerHeight });
        return;
      }

      const newPendingDamage = {
        id: `damage-${Date.now()}`,
        x: locationX,
        y: locationY,
      };
      
      console.log('✅ Setting pending damage:', newPendingDamage);
      setPendingDamage(newPendingDamage);
      setDamageType('scratch');
      setDamageSeverity('minor');
      setDamageDescription('');
      setPhotoUrl('');
      setShowDamageModal(true);
      console.log('✅ Modal should be visible now');
    }
  };

  const requestCameraPermission = async () => {
    if (Platform.OS === 'web') {
      return true; // Web doesn't need permissions
    }

    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert(
        'Permission Required',
        'Camera permission is required to take photos of damages.',
        [{ text: 'OK' }]
      );
      return false;
    }
    return true;
  };

  const requestMediaLibraryPermission = async () => {
    if (Platform.OS === 'web') {
      return true; // Web doesn't need permissions
    }

    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert(
        'Permission Required',
        'Media library permission is required to select photos.',
        [{ text: 'OK' }]
      );
      return false;
    }
    return true;
  };

  const handleTakePhoto = async () => {
    const hasPermission = await requestCameraPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.7,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await uploadPhoto(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  const handlePickImage = async () => {
    const hasPermission = await requestMediaLibraryPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.7,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await uploadPhoto(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to select image. Please try again.');
    }
  };

  const uploadPhoto = async (uri: string) => {
    try {
      setUploadingPhoto(true);
      const url = await uploadDamagePhoto(uri, 'receiving');
      setPhotoUrl(url);
      Alert.alert('Success', 'Photo uploaded successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
      Alert.alert('Error', 'Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!photoUrl) return;

    Alert.alert(
      'Remove Photo',
      'Are you sure you want to remove this photo?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteDamagePhoto(photoUrl);
              setPhotoUrl('');
            } catch (error) {
              console.error('Error deleting photo:', error);
              // Continue anyway - photo URL will be cleared
              setPhotoUrl('');
            }
          },
        },
      ]
    );
  };

  const handleAddDamage = () => {
    console.log('🔧 handleAddDamage called');
    console.log('🔧 pendingDamage:', pendingDamage);
    console.log('🔧 damageType:', damageType);
    console.log('🔧 damageSeverity:', damageSeverity);
    
    if (!pendingDamage || pendingDamage.x === undefined || pendingDamage.y === undefined) {
      console.error('❌ Invalid damage location:', pendingDamage);
      Alert.alert('Error', 'Invalid damage location');
      return;
    }

    const newDamage: VehicleDamage = {
      id: pendingDamage.id || `damage-${Date.now()}`,
      damageType: damageType,
      damageSeverity: damageSeverity,
      damageLocation: 'body', // General location
      damageDescription: damageDescription,
      x_coordinate: Math.round(pendingDamage.x),
      y_coordinate: Math.round(pendingDamage.y),
      photoUrl: photoUrl || undefined,
      createdAt: new Date().toISOString(),
    };

    console.log('✅ Adding new damage:', newDamage);
    console.log('✅ Current damages count:', damages.length);
    
    onDamagesChange([...damages, newDamage]);
    setShowDamageModal(false);
    setPendingDamage(null);
    setDamageDescription('');
    setPhotoUrl('');
    
    console.log('✅ Damage added successfully');
  };

  const handleRemoveDamage = (damageId: string) => {
    if (!editable) return;

    Alert.alert(
      'Remove Damage',
      'Are you sure you want to remove this damage marker?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            const updated = damages.filter((d) => d.id !== damageId);
            onDamagesChange(updated);
          },
        },
      ]
    );
  };

  const getSeverityColor = (severity: string) => {
    const found = DAMAGE_SEVERITY.find((s) => s.value === severity);
    return found?.color || '#44aa44';
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerText}>
          Vehicle Damage Markers ({markers.length})
        </Text>
        {editable && (
          <Text style={styles.instructionText}>
            Tap on the vehicle to mark damage
          </Text>
        )}
      </View>

      <Pressable
        ref={diagramRef}
        style={[
          styles.diagramContainer, 
          { 
            width: containerWidth, 
            height: containerHeight,
          }
        ]}
        onPress={handleDiagramPress}
        disabled={!editable}
      >
        <Image
          source={{ uri: VEHICLE_DIAGRAM_URL }}
          style={styles.vehicleImage}
          resizeMode="contain"
        />
        
        <Svg
          width={containerWidth}
          height={containerHeight}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {markers.map((marker, index) => {
            const color = getSeverityColor(marker.severity);
            return (
              <React.Fragment key={marker.id}>
                <Circle
                  cx={marker.x}
                  cy={marker.y}
                  r={8}
                  fill={color}
                  stroke="#000"
                  strokeWidth={2}
                  onPress={() => handleRemoveDamage(marker.id)}
                />
                <SvgText
                  x={marker.x}
                  y={marker.y + 3}
                  fontSize="10"
                  fill="#fff"
                  textAnchor="middle"
                  fontWeight="bold"
                >
                  {index + 1}
                </SvgText>
              </React.Fragment>
            );
          })}
        </Svg>
      </Pressable>

      {markers.length > 0 && (
        <View style={styles.damageList}>
          <Text style={styles.damageListTitle}>Damage Details:</Text>
          {markers.map((marker, index) => (
            <View key={marker.id} style={styles.damageItem}>
              <View
                style={[
                  styles.damageIndex,
                  { backgroundColor: getSeverityColor(marker.severity) },
                ]}
              >
                <Text style={styles.damageIndexText}>{index + 1}</Text>
              </View>
              <View style={styles.damageDetails}>
                <Text style={styles.damageTitle}>
                  {marker.type} - {marker.severity}
                </Text>
                {marker.description && (
                  <Text style={styles.damageDescription}>{marker.description}</Text>
                )}
                {marker.photoUrl && (
                  <View style={styles.damageThumbnailContainer}>
                    <Image source={{ uri: marker.photoUrl }} style={styles.damageThumbnail} />
                    <View style={styles.cameraIcon}>
                      <Ionicons name="camera" size={12} color={theme.colors.primary} />
                    </View>
                  </View>
                )}
              </View>
              {editable && (
                <TouchableOpacity
                  onPress={() => handleRemoveDamage(marker.id)}
                  style={styles.removeButton}
                >
                  <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                </TouchableOpacity>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Damage Entry Modal */}
      <Modal
        visible={showDamageModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowDamageModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Damage Marker</Text>
              <TouchableOpacity onPress={() => setShowDamageModal(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Damage Type *</Text>
                <View style={styles.optionGrid}>
                  {DAMAGE_TYPES.map((type) => (
                    <TouchableOpacity
                      key={type.value}
                      style={[
                        styles.optionButton,
                        damageType === type.value && styles.optionButtonActive,
                      ]}
                      onPress={() => setDamageType(type.value)}
                    >
                      <Text
                        style={[
                          styles.optionText,
                          damageType === type.value && styles.optionTextActive,
                        ]}
                      >
                        {type.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Severity *</Text>
                <View style={styles.optionGrid}>
                  {DAMAGE_SEVERITY.map((severity) => (
                    <TouchableOpacity
                      key={severity.value}
                      style={[
                        styles.optionButton,
                        damageSeverity === severity.value && styles.optionButtonActive,
                        { borderColor: severity.color },
                      ]}
                      onPress={() => setDamageSeverity(severity.value)}
                    >
                      <Text
                        style={[
                          styles.optionText,
                          damageSeverity === severity.value && styles.optionTextActive,
                        ]}
                      >
                        {severity.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Description (Optional)</Text>
                <TextInput
                  style={styles.textArea}
                  value={damageDescription}
                  onChangeText={setDamageDescription}
                  placeholder="Describe the damage..."
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                />
              </View>

              {/* Photo Capture Section */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Photo Evidence (Optional)</Text>
                
                {photoUrl ? (
                  <View style={styles.photoContainer}>
                    <Image source={{ uri: photoUrl }} style={styles.photoPreview} />
                    <TouchableOpacity
                      style={styles.removePhotoButton}
                      onPress={handleRemovePhoto}
                    >
                      <Ionicons name="close-circle" size={30} color={theme.colors.error} />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.photoButtons}>
                    <TouchableOpacity
                      style={styles.photoButton}
                      onPress={handleTakePhoto}
                      disabled={uploadingPhoto}
                    >
                      <Ionicons name="camera" size={24} color="#fff" />
                      <Text style={styles.photoButtonText}>Take Photo</Text>
                    </TouchableOpacity>
                    
                    <TouchableOpacity
                      style={[styles.photoButton, styles.photoButtonSecondary]}
                      onPress={handlePickImage}
                      disabled={uploadingPhoto}
                    >
                      <Ionicons name="images" size={24} color={theme.colors.primary} />
                      <Text style={[styles.photoButtonText, styles.photoButtonTextSecondary]}>
                        From Gallery
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
                
                {uploadingPhoto && (
                  <View style={styles.uploadingContainer}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <Text style={styles.uploadingText}>Uploading photo...</Text>
                  </View>
                )}
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowDamageModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.addButton]}
                onPress={handleAddDamage}
              >
                <Text style={styles.addButtonText}>Add Marker</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 8,
  },
  headerText: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  instructionText: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
  },
  diagramContainer: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.colors.white,
    overflow: 'hidden',
  },
  vehicleImage: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
  },
  damageList: {
    marginTop: 10,
  },
  damageListTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 5,
  },
  damageItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    backgroundColor: theme.colors.gray50,
    borderRadius: 4,
    marginBottom: 4,
  },
  damageIndex: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  damageIndexText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  damageDetails: {
    flex: 1,
  },
  damageTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    textTransform: 'capitalize',
  },
  damageDescription: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  removeButton: {
    padding: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  modalBody: {
    padding: 16,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  optionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    backgroundColor: theme.colors.white,
  },
  optionButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  optionText: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  optionTextActive: {
    color: theme.colors.white,
    fontWeight: '600',
  },
  textArea: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 6,
    padding: 10,
    fontSize: 13,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: theme.colors.gray100,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textSecondary,
  },
  addButton: {
    backgroundColor: theme.colors.primary,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.white,
  },
  // Photo-related styles
  photoContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: 4 / 3,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.colors.gray100,
  },
  photoPreview: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  removePhotoButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 15,
  },
  photoButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  photoButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: theme.colors.primary,
  },
  photoButtonSecondary: {
    backgroundColor: theme.colors.white,
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  photoButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.white,
  },
  photoButtonTextSecondary: {
    color: theme.colors.primary,
  },
  uploadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  uploadingText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  damageThumbnailContainer: {
    position: 'relative',
    marginTop: 4,
    width: 60,
    height: 45,
    borderRadius: 4,
    overflow: 'hidden',
  },
  damageThumbnail: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  cameraIcon: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 2,
  },
});
